import React, { useState, useEffect, useRef, useContext } from 'react';
import { apiPut, apiGet } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import { useForm } from 'react-hook-form';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useTranslation } from 'react-i18next';
import { preventMaxInput } from 'utils/validations';
import formValidation from '../../utils/formValidation';
import OImage from 'components/reusable/OImage';
import ErrorMessage from 'components/ErrorMessage';
import AuthContext from 'context/AuthContext';

const HROnboardingEdit = ({ allOnboarding, handleEdit, currentItem }) => {
  const { t } = useTranslation();
  const profileRef = useRef();
  const notification = useToastContext();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      name: currentItem?.name || '',
      email: currentItem?.email || '',
      mobile: currentItem?.mobile || '',
      city: currentItem?.city || '',
      address: currentItem?.address || '',
      profilePicture: currentItem?.profilePicture || ''
    }
  });

  const { user } = useContext(AuthContext);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(currentItem?.Country?._id || '');
  const [selectedState, setSelectedState] = useState(currentItem?.State?._id || '');
  const [passportPic, setPassportPic] = useState(currentItem?.profilePicture || '');
  const [bannerCardImg, setBannerCardImg] = useState({ 
    img: null, 
    url: currentItem?.profilePicture || null 
  });
  const [fileMsg, setFileMsg] = useState({ profilePicture: '' });

  // Set default values on component mount
  useEffect(() => {
    if (currentItem) {
      setValue('name', currentItem.name);
      setValue('email', currentItem.email);
      setValue('mobile', currentItem.mobile);
      setValue('city', currentItem.city);
      setValue('address', currentItem.address);
      setSelectedCountry(currentItem.Country?._id || '');
      setSelectedState(currentItem.State?._id || '');
      setPassportPic(currentItem.profilePicture || '');
    }
  }, [currentItem, setValue]);

  const checkFields = () => {
    const hasCountry = Boolean(selectedCountry);
    const hasState = Boolean(selectedState);
    const hasCity = Boolean(watch('city'));
    const hasProfilePicture = Boolean(watch('profilePicture')?.[0] || passportPic);

    return hasCountry && hasState && hasCity && !(hasProfilePicture && fileSizeExceedsLimit());
  };

  const fileSizeExceedsLimit = () => {
    const file = watch('profilePicture')?.[0];
    if (!file) return false;
    const fileSize = (file.size / (1024 * 1024)).toFixed(2);
    return fileSize > 3;
  };

  const onSubmit = async (data) => {
    try {
      if (!checkFields()) {
        if (!selectedCountry) setError('country', { type: 'manual', message: 'Please select country' });
        if (!selectedState) setError('state', { type: 'manual', message: 'Please select state' });
        if (!data.city) setError('city', { type: 'manual', message: 'Please enter city name' });
        return;
      }

      const countryObj = countries.find(item => item._id === selectedCountry);
      const formData = new FormData();

      // Add all editable fields to formData
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('mobile', data.mobile);
      formData.append('countryCode', countryObj?.countryCode);
      formData.append('address', data.address);
      formData.append('countryID', selectedCountry);
      formData.append('stateID', selectedState);
      formData.append('city', data.city);

      // Only append profile picture if a new one was selected
      if (bannerCardImg?.img) {
        formData.append('profilePicture', bannerCardImg.img);
      }

      const res = await apiPut(
        `${apiPath.onboardingEdit}/${currentItem._id}`,
        formData
      );

      if (res?.data?.success) {
        allOnboarding();
        handleEdit(false);
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message || 'Failed to update hr');
      }
    } catch (err) {
      console.error('Error updating hr:', err);
      notification.error('Failed to update hr');
    }
  };

  const validateFiles = (file) => {
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    return supportedTypes.includes(file.type);
  };

  const handleFileChangeImage = (e) => {
    const file = e?.target?.files[0];
    if (!file) return;

    const isValidImage = validateFiles(file);
    const fileSize = (file.size / (1024 * 1024)).toFixed(2);

    if (fileSize > 3) {
      notification.error('Please select image below 3Mb.');
      profileRef.current.value = '';
      return false;
    }

    if (!isValidImage) {
      notification.error('Only jpeg, jpg and png are supported.');
      profileRef.current.value = '';
      return;
    }

    setPassportPic(file);
    setBannerCardImg({
      url: URL.createObjectURL(file),
      img: file
    });
  };

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      setCountries(res?.data?.results?.allCountries || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadStates = async (countryId) => {
    try {
      setLoadingStates(true);
      const res = await apiGet(`/v1/user/states/${countryId}`, {});
      setStates(res?.data?.results || []);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    getCountryList();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadStates(selectedCountry);
    } else {
      setStates([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  return (
    <div className="overflow-y-auto fixed justify-center items-center flex overflow-x-hidden inset-0 z-50 outline-none focus:outline-none">
      <div className="mx-auto relative w-auto my-6 max-w-3xl">
        <div className="sm:py-8 md:py-4 sm:px-2 px-7">
          <div className="outline-none focus:outline-none border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white min-w-[762px]">
            <div className="rounded-t flex items-center justify-between p-5 border-b border-solid border-slate-200">
              <h3 className="text-xl font-semibold">
                {t('EDIT_HR_ONBOARDING')}
              </h3>
              <button
                onClick={() => handleEdit(false)}
                title="Close"
                className="h-8 w-8 float-right ml-auto flex items-center justify-center text-black border-2 rounded-full text-3xl leading-none font-extralight outline-none focus:outline-none"
              >
                <span className="text-4xl text-[#B8BBBF]" title="Close">
                  ×
                </span>
              </button>
            </div>
            <div className="p-6 relative flex-auto">
              <div className="grid sm:grid-cols-2">
                {/* Name Field */}
                <div className="sm:py-8 md:py-4 sm:px-2 px-7">
                  <OInputField
                    inputLabel={<>{t('NAME')}<span className="text-red-500">*</span></>}
                    wrapperClassName="z-0 relative w-full group"
                    name="name"
                    onInput={(e) => preventMaxInput(e, 50)}
                    type="text"
                    maxLength={50}
                    register={register('name', formValidation['name'])}
                    errors={errors}
                  />
                </div>

                {/* Email Field */}
                <div className="sm:py-8 md:py-4 sm:px-2 px-7">
                  <OInputField
                    inputLabel={<>{t('EMAIL')}<span className="text-red-500">*</span></>}
                    wrapperClassName="z-0 relative w-full group"
                    name="email"
                    onInput={(e) => preventMaxInput(e, 50)}
                    type="text"
                    maxLength={50}
                    register={register('email', formValidation['email'])}
                    errors={errors}
                  />
                </div>

                {/* Country Field */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="group relative z-0 w-full">
                    <select
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none peer"
                      name="country"
                      id="country"
                      value={selectedCountry}
                      onChange={(e) => {
                        setSelectedCountry(e.target.value);
                        clearErrors('country');
                      }}
                    >
                      <option value="">{t('SELECT_COUNTRY')} <span className="text-red-500">*</span></option>
                      {countries?.map((country) => (
                        <option key={country._id} value={country._id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <label
                      className="duration-300 transform -translate-y-6 scale-75 peer-focus:font-normal absolute text-sm text-[#A5A5A5] top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                      htmlFor="country"
                    >
                      {t('COUNTRY')}<span className="text-red-500">*</span>
                    </label>
                    {errors.country && <ErrorMessage message={errors.country.message} />}
                  </div>
                </div>

                {/* Mobile Field with Country Code */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7 flex">
                  {countries?.length > 0 && countries?.map((country) => (
                    selectedCountry === country._id && (
                      <div
                        key={country._id}
                        className="w-[50px] h-[56px] mr-3 block py-4 px-3 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none"
                      >
                        +{country.countryCode}
                      </div>
                    )
                  ))}
                  <div className="relative z-0 w-full group">
                    <OInputField
                      wrapperClassName="relative z-0 w-full group"
                      type="number"
                      name="mobile"
                      id="mobile"
                      inputLabel={<>{t('MOBILE')}<span className="text-red-500">*</span></>}
                      min={0}
                      onKeyDown={(e) => {
                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                      }}
                      onInput={(e) => preventMaxInput(e, 15)}
                      register={register('mobile', formValidation['mobile'])}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* State Field */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="group relative z-0 w-full">
                    <select
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none peer"
                      name="state"
                      id="state"
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        clearErrors('state');
                      }}
                      disabled={!selectedCountry || loadingStates}
                    >
                      <option value="">{t('SELECT_STATE')} <span className="text-red-500">*</span></option>
                      {loadingStates ? (
                        <option value="" disabled>Loading states...</option>
                      ) : (
                        states?.map((state) => (
                          <option key={state._id} value={state._id}>
                            {state.name}
                          </option>
                        ))
                      )}
                    </select>
                    <label
                      className="duration-300 transform -translate-y-6 scale-75 peer-focus:font-normal absolute text-sm text-[#A5A5A5] top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                      htmlFor="state"
                    >
                      {t('STATE')}<span className="text-red-500">*</span>
                    </label>
                    {errors.state && <ErrorMessage message={errors.state.message} />}
                  </div>
                </div>

                {/* City Field */}
                <div className="sm:py-8 md:py-4 sm:px-2 px-7">
                  <OInputField
                    inputLabel={<>{t('CITY')}<span className="text-red-500">*</span></>}
                    wrapperClassName="z-0 relative w-full group"
                    name="city"
                    onInput={(e) => preventMaxInput(e, 100)}
                    type="text"
                    maxLength={100}
                    register={register('city', {
                      required: 'Please enter city name',
                      minLength: {
                        value: 2,
                        message: 'City name must be at least 2 characters',
                      },
                      maxLength: {
                        value: 100,
                        message: 'City name cannot exceed 100 characters',
                      },
                      onChange: (e) => clearErrors('city'),
                    })}
                    errors={errors}
                    disabled={!selectedState}
                  />
                </div>

                {/* Address Field */}
                <div className="sm:py-8 md:py-4 sm:px-2 px-7">
                  <OInputField
                    inputLabel={<>{t('ADDRESS')}<span className="text-red-500">*</span></>}
                    wrapperClassName="z-0 relative w-full group"
                    name="address"
                    onInput={(e) => preventMaxInput(e, 300)}
                    type="text"
                    maxLength={300}
                    register={register('address', formValidation['address'])}
                    errors={errors}
                  />
                </div>

                {/* Profile Picture Field */}
                <div className="px-7 md:py-4 sm:px-2 sm:py-8">
                  <div className="relative w-full group z-0">
                    <div className="bg-transparent block py-3 h-14 px-3 w-full text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] appearance-none">
                      <input
                        type="file"
                        ref={profileRef}
                        onChange={handleFileChangeImage}
                        name="profilePicture"
                        className="form-control"
                        accept="image/png, image/jpeg, image/jpg"
                      />
                    </div>
                    <label
                      htmlFor="profilePicture"
                      className="top-1 left-0 peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('PROFILE_PICTURE')} (Only image, up to 3Mb)
                    </label>
                    <div className="bg-white flex items-center">
                      {(bannerCardImg?.url || passportPic) && (
                        <OImage
                          src={bannerCardImg?.url || passportPic}
                          className="mt-5 w-24 h-24"
                          alt="Profile"
                        />
                      )}
                    </div>
                    <ErrorMessage message={fileMsg.profilePicture} />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-solid flex items-center justify-center p-6 border-slate-200 rounded-b">
              <button
                className="outline-none text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
                onClick={() => handleEdit(false)}
                type="button"
              >
                {t('CANCEL')}
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                className="text-sm px-8 py-2.5 rounded bg-LightBlue text-white font-normal shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                type="submit"
              >
                 {t('O_SAVE')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="inset-0 z-40 opacity-25 fixed bg-black" /> */}
    </div>
  );
};

export default HROnboardingEdit;



// import React, { useState, useEffect, useRef, useContext } from 'react';
// import { apiPut, apiGet } from '../../utils/apiFetch';
// import apiPath from '../../utils/pathObj';
// import { useForm } from 'react-hook-form';
// import useToastContext from 'hooks/useToastContext';
// import OInputField from 'components/reusable/OInputField';
// import { useTranslation } from 'react-i18next';
// import { preventMaxInput } from 'utils/validations';
// import formValidation from '../../utils/formValidation';
// import OImage from 'components/reusable/OImage';
// import ErrorMessage from 'components/ErrorMessage';
// import AuthContext from 'context/AuthContext';

// const AccountantOnboardingEdit = ({ allAccountants, handleEdit, currentItem }) => {
//   const { t } = useTranslation();
//   const profileRef = useRef();
//   const notification = useToastContext();
//   const {
//     register,
//     handleSubmit,
//     setValue,
//     watch,
//     setError,
//     clearErrors,
//     formState: { errors }
//   } = useForm({
//     mode: 'onChange',
//     shouldFocusError: true,
//     defaultValues: {
//       name: currentItem?.name || '',
//       email: currentItem?.email || '',
//       mobile: currentItem?.mobile || '',
//       city: currentItem?.city || '',
//       address: currentItem?.address || '',
//       profilePicture: currentItem?.profilePicture || ''
//     }
//   });

//   const { user } = useContext(AuthContext);
//   const [countries, setCountries] = useState([]);
//   const [states, setStates] = useState([]);
//   const [loadingStates, setLoadingStates] = useState(false);
//   const [selectedCountry, setSelectedCountry] = useState(currentItem?.Country?._id || '');
//   const [selectedState, setSelectedState] = useState(currentItem?.State?._id || '');
//   const [passportPic, setPassportPic] = useState(currentItem?.profilePicture || '');
//   const [bannerCardImg, setBannerCardImg] = useState({ 
//     img: null, 
//     url: currentItem?.profilePicture || null 
//   });
//   const [fileMsg, setFileMsg] = useState({ profilePicture: '' });

//   // Set default values on component mount
//   useEffect(() => {
//     if (currentItem) {
//       setValue('name', currentItem.name);
//       setValue('email', currentItem.email);
//       setValue('mobile', currentItem.mobile);
//       setValue('city', currentItem.city);
//       setValue('address', currentItem.address);
//       setSelectedCountry(currentItem.Country?._id || '');
//       setSelectedState(currentItem.State?._id || '');
//       setPassportPic(currentItem.profilePicture || '');
//     }
//   }, [currentItem, setValue]);

//   const checkFields = () => {
//     const hasCountry = Boolean(selectedCountry);
//     const hasState = Boolean(selectedState);
//     const hasCity = Boolean(watch('city'));
//     const hasProfilePicture = Boolean(watch('profilePicture')?.[0] || passportPic);

//     return hasCountry && hasState && hasCity && !(hasProfilePicture && fileSizeExceedsLimit());
//   };

//   const fileSizeExceedsLimit = () => {
//     const file = watch('profilePicture')?.[0];
//     if (!file) return false;
//     const fileSize = (file.size / (1024 * 1024)).toFixed(2);
//     return fileSize > 3;
//   };

//   const onSubmit = async (data) => {
//     try {
//       if (!checkFields()) {
//         if (!selectedCountry) setError('country', { type: 'manual', message: 'Please select country' });
//         if (!selectedState) setError('state', { type: 'manual', message: 'Please select state' });
//         if (!data.city) setError('city', { type: 'manual', message: 'Please enter city name' });
//         return;
//       }

//       const countryObj = countries.find(item => item._id === selectedCountry);
//       const formData = new FormData();

//       // Add all editable fields to formData
//       formData.append('name', data.name);
//       formData.append('email', data.email);
//       formData.append('mobile', data.mobile);
//       formData.append('countryCode', countryObj?.countryCode);
//       formData.append('address', data.address);
//       formData.append('countryID', selectedCountry);
//       formData.append('stateID', selectedState);
//       formData.append('city', data.city);

//       // Only append profile picture if a new one was selected
//       if (bannerCardImg?.img) {
//         formData.append('profilePicture', bannerCardImg.img);
//       }

//       const res = await apiPut(
//         `${apiPath.accountantEdit}/${currentItem._id}`,
//         formData
//       );

//       if (res?.data?.success) {
//         allAccountants();
//         handleEdit(false);
//         notification.success(res?.data?.message);
//       } else {
//         notification.error(res?.data?.message || 'Failed to update accountant');
//       }
//     } catch (err) {
//       console.error('Error updating accountant:', err);
//       notification.error('Failed to update accountant');
//     }
//   };

//   const validateFiles = (file) => {
//     const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
//     return supportedTypes.includes(file.type);
//   };

//   const handleFileChangeImage = (e) => {
//     const file = e?.target?.files[0];
//     if (!file) return;

//     const isValidImage = validateFiles(file);
//     const fileSize = (file.size / (1024 * 1024)).toFixed(2);

//     if (fileSize > 3) {
//       notification.error('Please select image below 3Mb.');
//       profileRef.current.value = '';
//       return false;
//     }

//     if (!isValidImage) {
//       notification.error('Only jpeg, jpg and png are supported.');
//       profileRef.current.value = '';
//       return;
//     }

//     setPassportPic(file);
//     setBannerCardImg({
//       url: URL.createObjectURL(file),
//       img: file
//     });
//   };

//   const getCountryList = async () => {
//     try {
//       const res = await apiGet(apiPath.getCountryList, {});
//       setCountries(res?.data?.results?.allCountries || []);
//     } catch (error) {
//       console.error('Error loading countries:', error);
//     }
//   };

//   const loadStates = async (countryId) => {
//     try {
//       setLoadingStates(true);
//       const res = await apiGet(`/v1/user/states/${countryId}`, {});
//       setStates(res?.data?.results || []);
//     } catch (error) {
//       console.error('Error loading states:', error);
//     } finally {
//       setLoadingStates(false);
//     }
//   };

//   useEffect(() => {
//     getCountryList();
//   }, []);

//   useEffect(() => {
//     if (selectedCountry) {
//       loadStates(selectedCountry);
//     } else {
//       setStates([]);
//       setSelectedState('');
//     }
//   }, [selectedCountry]);

//   return (
//     <div className="overflow-y-auto fixed justify-center items-center flex overflow-x-hidden inset-0 z-50 outline-none focus:outline-none">
//       <div className="mx-auto relative w-auto my-6 max-w-3xl">
//         <div className="sm:py-8 md:py-4 sm:px-2 px-7">
//           <div className="outline-none focus:outline-none border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white min-w-[762px]">
//             <div className="rounded-t flex items-center justify-between p-5 border-b border-solid border-slate-200">
//               <h3 className="text-xl font-semibold">
//                 {t('EDIT_ACCOUNTANT')}
//               </h3>
//               <button
//                 onClick={() => handleEdit(false)}
//                 title="Close"
//                 className="h-8 w-8 float-right ml-auto flex items-center justify-center text-black border-2 rounded-full text-3xl leading-none font-extralight outline-none focus:outline-none"
//               >
//                 <span className="text-4xl text-[#B8BBBF]" title="Close">
//                   ×
//                 </span>
//               </button>
//             </div>
//             <div className="p-6 relative flex-auto">
//               <div className="grid sm:grid-cols-2">
//                 {/* Name Field */}
//                 <div className="sm:py-8 md:py-4 sm:px-2 px-7">
//                   <OInputField
//                     inputLabel={<>{t('NAME')}<span className="text-red-500">*</span></>}
//                     wrapperClassName="z-0 relative w-full group"
//                     name="name"
//                     onInput={(e) => preventMaxInput(e, 50)}
//                     type="text"
//                     maxLength={50}
//                     register={register('name', formValidation['name'])}
//                     errors={errors}
//                   />
//                 </div>

//                 {/* Email Field */}
//                 <div className="sm:py-8 md:py-4 sm:px-2 px-7">
//                   <OInputField
//                     inputLabel={<>{t('EMAIL')}<span className="text-red-500">*</span></>}
//                     wrapperClassName="z-0 relative w-full group"
//                     name="email"
//                     onInput={(e) => preventMaxInput(e, 50)}
//                     type="text"
//                     maxLength={50}
//                     register={register('email', formValidation['email'])}
//                     errors={errors}
//                   />
//                 </div>

//                 {/* Country Field */}
//                 <div className="md:py-4 sm:px-2 sm:py-8 px-7">
//                   <div className="group relative z-0 w-full">
//                     <select
//                       className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none peer"
//                       name="country"
//                       id="country"
//                       value={selectedCountry}
//                       onChange={(e) => {
//                         setSelectedCountry(e.target.value);
//                         clearErrors('country');
//                       }}
//                     >
//                       <option value="">{t('SELECT_COUNTRY')} <span className="text-red-500">*</span></option>
//                       {countries?.map((country) => (
//                         <option key={country._id} value={country._id}>
//                           {country.name}
//                         </option>
//                       ))}
//                     </select>
//                     <label
//                       className="duration-300 transform -translate-y-6 scale-75 peer-focus:font-normal absolute text-sm text-[#A5A5A5] top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//                       htmlFor="country"
//                     >
//                       {t('COUNTRY')}<span className="text-red-500">*</span>
//                     </label>
//                     {errors.country && <ErrorMessage message={errors.country.message} />}
//                   </div>
//                 </div>

//                 {/* Mobile Field with Country Code */}
//                 <div className="md:py-4 sm:px-2 sm:py-8 px-7 flex">
//                   {countries?.length > 0 && countries?.map((country) => (
//                     selectedCountry === country._id && (
//                       <div
//                         key={country._id}
//                         className="w-[50px] h-[56px] mr-3 block py-4 px-3 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none"
//                       >
//                         +{country.countryCode}
//                       </div>
//                     )
//                   ))}
//                   <div className="relative z-0 w-full group">
//                     <OInputField
//                       wrapperClassName="relative z-0 w-full group"
//                       type="number"
//                       name="mobile"
//                       id="mobile"
//                       inputLabel={<>{t('MOBILE')}<span className="text-red-500">*</span></>}
//                       min={0}
//                       onKeyDown={(e) => {
//                         if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
//                       }}
//                       onInput={(e) => preventMaxInput(e, 15)}
//                       register={register('mobile', formValidation['mobile'])}
//                       errors={errors}
//                     />
//                   </div>
//                 </div>

//                 {/* State Field */}
//                 <div className="md:py-4 sm:px-2 sm:py-8 px-7">
//                   <div className="group relative z-0 w-full">
//                     <select
//                       className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none peer"
//                       name="state"
//                       id="state"
//                       value={selectedState}
//                       onChange={(e) => {
//                         setSelectedState(e.target.value);
//                         clearErrors('state');
//                       }}
//                       disabled={!selectedCountry || loadingStates}
//                     >
//                       <option value="">{t('SELECT_STATE')} <span className="text-red-500">*</span></option>
//                       {loadingStates ? (
//                         <option value="" disabled>Loading states...</option>
//                       ) : (
//                         states?.map((state) => (
//                           <option key={state._id} value={state._id}>
//                             {state.name}
//                           </option>
//                         ))
//                       )}
//                     </select>
//                     <label
//                       className="duration-300 transform -translate-y-6 scale-75 peer-focus:font-normal absolute text-sm text-[#A5A5A5] top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//                       htmlFor="state"
//                     >
//                       {t('STATE')}<span className="text-red-500">*</span>
//                     </label>
//                     {errors.state && <ErrorMessage message={errors.state.message} />}
//                   </div>
//                 </div>

//                 {/* City Field */}
//                 <div className="sm:py-8 md:py-4 sm:px-2 px-7">
//                   <OInputField
//                     inputLabel={<>{t('CITY')}<span className="text-red-500">*</span></>}
//                     wrapperClassName="z-0 relative w-full group"
//                     name="city"
//                     onInput={(e) => preventMaxInput(e, 100)}
//                     type="text"
//                     maxLength={100}
//                     register={register('city', {
//                       required: 'Please enter city name',
//                       minLength: {
//                         value: 2,
//                         message: 'City name must be at least 2 characters',
//                       },
//                       maxLength: {
//                         value: 100,
//                         message: 'City name cannot exceed 100 characters',
//                       },
//                       onChange: (e) => clearErrors('city'),
//                     })}
//                     errors={errors}
//                     disabled={!selectedState}
//                   />
//                 </div>

//                 {/* Address Field */}
//                 <div className="sm:py-8 md:py-4 sm:px-2 px-7">
//                   <OInputField
//                     inputLabel={<>{t('ADDRESS')}<span className="text-red-500">*</span></>}
//                     wrapperClassName="z-0 relative w-full group"
//                     name="address"
//                     onInput={(e) => preventMaxInput(e, 300)}
//                     type="text"
//                     maxLength={300}
//                     register={register('address', formValidation['address'])}
//                     errors={errors}
//                   />
//                 </div>

//                 {/* Profile Picture Field */}
//                 <div className="px-7 md:py-4 sm:px-2 sm:py-8">
//                   <div className="relative w-full group z-0">
//                     <div className="bg-transparent block py-3 h-14 px-3 w-full text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] appearance-none">
//                       <input
//                         type="file"
//                         ref={profileRef}
//                         onChange={handleFileChangeImage}
//                         name="profilePicture"
//                         className="form-control"
//                         accept="image/png, image/jpeg, image/jpg"
//                       />
//                     </div>
//                     <label
//                       htmlFor="profilePicture"
//                       className="top-1 left-0 peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//                     >
//                       {t('PROFILE_PICTURE')} (Only image, up to 3Mb)
//                     </label>
//                     <div className="bg-white flex items-center">
//                       {(bannerCardImg?.url || passportPic) && (
//                         <OImage
//                           src={bannerCardImg?.url || passportPic}
//                           className="mt-5 w-24 h-24"
//                           alt="Profile"
//                         />
//                       )}
//                     </div>
//                     <ErrorMessage message={fileMsg.profilePicture} />
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div className="border-t border-solid flex items-center justify-center p-6 border-slate-200 rounded-b">
//               <button
//                 className="outline-none text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
//                 onClick={() => handleEdit(false)}
//                 type="button"
//               >
//                 {t('CANCEL')}
//               </button>
//               <button
//                 onClick={handleSubmit(onSubmit)}
//                 className="text-sm px-8 py-2.5 rounded bg-LightBlue text-white font-normal shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
//                 type="submit"
//               >
//                 {t('SAVE_CHANGES')}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="inset-0 z-40 opacity-25 fixed bg-black" />
//     </div>
//   );
// };

// export default AccountantOnboardingEdit;